import { homApi } from '~/redux/apis/hom'
import { notificationApi } from '~/redux/apis/notification-api'
import { rewardApi } from '~/redux/apis/reward-api'
import { CURRICULUM_TYPE } from '~/redux/constants/tags.const'
import { CurriculumProgressArray } from '~/curriculum/types/curriculum-progress.type'

import {
  CompletedElsewhereMap,
  CurriculumGradeDataType,
  CurriculumProgressResponseByTypeDto,
  CurriculumProgressPercentageResponseByTypeDto,
  PracticeActivityProgressBody,
  LearningActivitiesProgressBody,
  SubtopicProgressResponse,
  TopicProgressEntry,
  TopicProgressResponse,
} from '../types/curriculum.types'
import {
  AcknowledgeProgressMigrationModalResponse,
  ProgressMigrationModalStatusResponse,
} from '../types/progress-migration.types'
import { generateUserCurriculumCompletedProgress } from '../utils/generateUserGradeBootcampLevelProgress'

/**
 * These endpoints fire once per question, but curriculum grants xp per completed set — and the
 * activity modal sends `reward` on every submission, zeroed until the set is done
 * (`ActivityDynamicLoaderModal.tsx:152-167`). So the test is on the values, not on the field being
 * present, or this would refetch on every answer.
 */
const awardsSomething = (reward?: { gems: number; kudos: number; xp: number }): boolean =>
  Boolean(reward && (reward.xp > 0 || reward.gems > 0 || reward.kudos > 0))

/**
 * `rewardApi` and `notificationApi` are their own `createApi`s, so a `homApi` tag never reaches them
 * — xp has to be invalidated by hand, the way bootcamp and math-vault already do it. Without this
 * the client keeps the xp it loaded with for the whole activity, and a level-up only surfaces on the
 * next screen that refetches rewards (#869f1d33w).
 */
const invalidateRewards = async (
  queryFulfilled: Promise<unknown>,
  dispatch: (action: unknown) => unknown,
): Promise<void> => {
  try {
    await queryFulfilled
  } catch {
    // A failed progress post credits no xp, so there is nothing to refetch.
    return
  }

  dispatch(rewardApi.util.invalidateTags(['Rewards']))
  dispatch(notificationApi.util.invalidateTags(['notification']))
}

export const curriculumProgressApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    setCurriculumAiProgress: builder.mutation<{ success: boolean }, { curriculumLevelId?: string }>({
      query: (data) => ({
        url: '/v1/curriculum/progress/ai-prompt',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['curriculumLevelProgress'],
    }),
    setEncyclopediaProgress: builder.mutation<{ success: boolean }, { path?: string }>({
      query: (data) => ({
        url: '/v1/curriculum/progress/encyclopedia',
        method: 'POST',
        body: data,
      }),
    }),
    setLearningActivityProgress: builder.mutation<
      { ActivityType: string; IsComplete: boolean },
      LearningActivitiesProgressBody
    >({
      query: (data) => ({
        url: '/v1/progress/learning-activities',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_V2],
      onQueryStarted: (arg, { dispatch, queryFulfilled }) =>
        awardsSomething(arg.reward) ? invalidateRewards(queryFulfilled, dispatch) : undefined,
    }),
    setPracticeActivityProgress: builder.mutation<
      { ActivityStatus: string; DifficultyStatus: string; Accuracy: number },
      PracticeActivityProgressBody
    >({
      query: (data) => ({
        url: '/v1/progress/practice-activity',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_V2],
      onQueryStarted: (arg, { dispatch, queryFulfilled }) =>
        awardsSomething(arg.reward) ? invalidateRewards(queryFulfilled, dispatch) : undefined,
    }),
    getCurriculumProgress: builder.query<CurriculumGradeDataType, { gradeData: CurriculumGradeDataType }>({
      query: () => ({
        url: '/v1/curriculum/progress',
        method: 'GET',
      }),
      transformResponse: (response: string[], meta, arg) => {
        const { gradeData } = arg
        return generateUserCurriculumCompletedProgress(gradeData, response)
      },
      providesTags: ['curriculumLevelProgress'],
    }),
    getCurriculumProgressPercentage: builder.query<
      CurriculumProgressPercentageResponseByTypeDto,
      { countryCode: string }
    >({
      query: (params) => ({
        url: '/v1/curriculum/progress-percentage',
        method: 'GET',
        params,
      }),
      providesTags: ['curriculumLevelProgress'],
    }),
    getEncyclopediaProgress: builder.query<CurriculumProgressResponseByTypeDto, { path: string }>({
      query: (params) => ({
        url: '/v1/curriculum/progress/encyclopedia',
        method: 'GET',
        params,
      }),
    }),
    getCurriculumProgressSummary: builder.query<CurriculumProgressArray, { country: string; grade_id?: string }>({
      query: (params) => ({
        url: '/v1/progress/get-summary',
        method: 'GET',
        params,
      }),
      providesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_V2],
    }),
    getCurriculumSubtopicProgressSummary: builder.query<SubtopicProgressResponse, { subtopicId: string }>({
      query: (params) => ({
        url: '/v1/progress/get-sub-topic-summary',
        method: 'GET',
        params,
      }),
      providesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_V2],
    }),
    getCurriculumTopicProgressSummary: builder.query<
      TopicProgressResponse,
      { topicId: string; includeCrossGrade?: boolean; gradeId?: string; levelIds?: string }
    >({
      query: (params) => ({
        url: '/v1/progress/get-topic-summary',
        method: 'GET',
        params,
      }),
      // Backend returns topic entries keyed by id with an optional `completedElsewhere` sibling.
      // Split them into a clean envelope so consumers never index the map by topic id.
      transformResponse: (
        response: { [topicId: string]: TopicProgressEntry } & { completedElsewhere?: CompletedElsewhereMap },
      ): TopicProgressResponse => {
        const { completedElsewhere, ...topics } = response
        return { topics, completedElsewhere }
      },
      providesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_V2],
    }),
    getProgressMigrationModalStatus: builder.query<ProgressMigrationModalStatusResponse, void>({
      query: () => ({
        url: '/v1/curriculum/progress-migration/modal-status',
        method: 'GET',
      }),
      providesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_MIGRATION_MODAL],
    }),
    acknowledgeProgressMigrationModal: builder.mutation<AcknowledgeProgressMigrationModalResponse, void>({
      query: () => ({
        url: '/v1/curriculum/progress-migration/modal-status',
        method: 'PATCH',
      }),
      invalidatesTags: [CURRICULUM_TYPE.CURRICULUM_PROGRESS_MIGRATION_MODAL],
    }),
  }),
})

export const {
  useSetCurriculumAiProgressMutation,
  useSetEncyclopediaProgressMutation,
  useSetLearningActivityProgressMutation,
  useSetPracticeActivityProgressMutation,
  useGetCurriculumProgressQuery,
  useGetEncyclopediaProgressQuery,
  useGetCurriculumProgressPercentageQuery,
  useGetCurriculumProgressSummaryQuery,
  useGetCurriculumSubtopicProgressSummaryQuery,
  useGetCurriculumTopicProgressSummaryQuery,
  useGetProgressMigrationModalStatusQuery,
  useAcknowledgeProgressMigrationModalMutation,
} = curriculumProgressApi
